import { Linking, ScrollView, StyleSheet, View } from "react-native";
import { fp, spH, spV, wp } from "utils/normalizeV2";

import ParentScrollView from "components/ParentScrollView";
import VText from "components/VText";
import Heading from "components/Heading";

export default function PrivacyPolicy({ _scrollRef }) {
    return (
        <ParentScrollView _scrollRef={_scrollRef}>
            <View style={styles.container}>
                <View style={styles.headingContainer}>
                    <Heading type="h1" style={styles.headingText}>Privacy Policy</Heading>
                </View>
                <View style={styles.dateContainer}>
                    <Heading type="h2" style={styles.dateText}>Effective as of: </Heading>
                    <Heading type="h2" style={styles.dateValue}>OCTOBER 1, 2023</Heading>
                </View>
                <View style={styles.textContainer}>
                    <VText style={styles.paragraphText}>
                        This privacy policy (the “Privacy Policy”) applies to the Vetir website (the “App”), owned and
                        operated by Vetir, Inc. (“Vetir,” “we,” “us,” or “our”). We have created this Privacy Policy to
                        tell you what information the App collects, how we use that information, and who we will share
                        that information with, if at all. This Privacy Policy does not address the privacy practices of
                        any third parties that we do not own, control, or are affiliated with. Capitalized terms not
                        defined in this Privacy Policy will have the meaning set forth in our Terms of Use. By visiting
                        and/or using our App, you are agreeing to the terms of this Privacy Policy and the accompanying
                        Terms of Use. We encourage you to read the Privacy Policy, and to use the information it
                        contains to help you make informed decisions.
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>INFORMATION WE COLLECT OR RECEIVE:</Heading>
                    <VText style={styles.paragraphText}>
                        Personal Information: We collect personally identifiable information only if you provide it to
                        us voluntarily via email or other means.
                    </VText>
                    <VText style={styles.paragraphText}>
                        Google User Data: If you grant us access to your Google account (in the Closet Section of App),
                        we collect Gmail inbox data related to e-commerce invoices to provide our digital closet
                        service. This data includes order details such as item names, prices, and purchase dates.
                    </VText>
                    <VText style={styles.paragraphText}>
                        Automatically Collected Information: When you use the App, we may automatically collect certain
                        technical information, including device type, IP address. We are not saving this information in
                        any of our databases.
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>HOW WE USE GOOGLE USER DATA</Heading>
                    <VText style={styles.paragraphText}>
                        {`We use Google user data exclusively to enhance the functionality of our App by providing the following services:\n1. Extracting e-commerce invoice details from Gmail to digitalize users' wardrobes.\n2. Organizing and categorizing clothing items for personalized outfit recommendations. We do not use Google user data for any purposes other than to improve and provide these core functionalities.`}
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>WITH WHOM WE SHARE GOOGLE USER DATA</Heading>
                    <VText style={styles.paragraphText}>We do not sell, rent, or trade Google user data.</VText>
                    <Heading type="h2" style={styles.boldParagraphText}>
                        HOW TO OPT OUT OF THIRD-PARTY INTEREST-BASED ADVERTISING:
                    </Heading>
                    <VText style={styles.paragraphText}>
                        If you wish to opt out of third-party interest-based advertising, please visit Network
                        Advertising Initiative and About Ads for details on how to do so. Additionally, users of mobile
                        Devices can follow the below instructions:
                    </VText>
                    <VText style={styles.boldParagraphText}>HOW INFORMATION IS USED AND SHARED:</VText>
                    <VText style={styles.paragraphText}>
                        You authorize us to use the Personal Information, Payment Information, Geolocational
                        Information, and the Other Information (collectively, the “Information”) to:
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>
                        ACCESSING AND MODIFYING INFORMATION AND COMMUNICATION PREFERENCES:
                    </Heading>
                    <VText style={styles.paragraphText}>
                        If you have provided us any Personal Information, you may access, remove, review, and/or make
                        changes to the same by contacting us as set forth below…
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>HOW WE PROTECT YOUR INFORMATION:</Heading>
                    <VText style={styles.paragraphText}>
                        We take commercially reasonable steps to protect the Information from loss, misuse, and
                        unauthorized access, disclosure, alteration, or destruction…
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>IMPORTANT NOTICES TO NON-U.S. RESIDENTS:</Heading>
                    <VText style={styles.paragraphText}>
                        The App and its servers are operated in the United States. If you are located outside of the
                        United States, please be aware that your Information, including your Personal Information, may
                        be transferred to, processed, maintained, and used on computers, servers, and systems located
                        outside of your state, province, country, or other governmental jurisdiction where the privacy
                        laws may not be as protective as those in your jurisdiction.
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>CHANGES TO THIS PRIVACY POLICY:</Heading>
                    <VText style={styles.paragraphText}>
                        This Privacy Policy is effective as of the date stated at the top of this Privacy Policy. We may
                        change this Privacy Policy from time to time. Any such changes will be posted on the App. By
                        accessing the App after we make any such changes to this Privacy Policy, you are deemed to have
                        accepted such changes.
                    </VText>
                    <Heading type="h2" style={styles.boldParagraphText}>HOW TO CONTACT US:</Heading>
                    <VText style={styles.paragraphText}>
                        If you have questions about this Privacy Policy, please e-mail us at
                        <VText
                            style={styles.contactEmail}
                            onPress={() => {
                                Linking.openURL("mailto:Stylist@vetirapp.com?subject=Privacy Policy");
                            }}
                        >
                            {" "}
                            Stylist@vetirapp.com
                        </VText>{" "}
                        with “Privacy Policy” in the subject line.
                    </VText>
                </View>
            </View>
        </ParentScrollView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: "white",
        paddingBottom: "1vh",
        width: "auto",
        marginTop: "4vh",
        marginHorizontal: "4vw",
    },
    headingContainer: {
        alignItems: "center",
    },
    headingText: {
        textAlign: "center",
        fontSize: "2rem",
        fontFamily: "LibreBodoni",
        fontWeight: "350",
    },
    dateContainer: {
        marginTop: "4vh",
        flexDirection: "row",
        justifyContent: "left",
        gap: "0.0625rem",
    },
    dateText: {
        fontSize: "1rem",
        fontWeight: "550",
        textAlign: "center",
    },
    dateValue: {
        fontSize: "1rem",
        fontWeight: "350",
        textAlign: "center",
    },
    textContainer: {
        marginTop: "2vh",
    },
    paragraphText: {
        padding: 0,
        fontSize: "1rem",
        fontWeight: "350",
        lineHeight: "1.5rem",
        textAlign: "left",
        marginBottom: "4vh",
    },
    boldParagraphText: {
        fontSize: "1rem",
        lineHeight: "1.5rem",
        textAlign: "left",
        fontWeight: "500",
        marginBottom: "0.5vh",
    },
    contactEmail: {
        color: "#000000",
        // textDecorationLine: "underline",
    },
});
